// @ts-check
import React from 'react';
import { Divider, Icon, Grid, Typography, Box } from '@material-ui/core';
import PropTypes from 'prop-types';
import { additionalPaymentsPropType } from 'utils/PropTypesValues';
import { ShipmentMethodPropTypes, getCardIconByCardType, formatNumberToUSD } from 'utils';
import { convertStringAmountToNumber } from 'modules/clients/utils';
import { ReceiptFields } from 'sharedComponents';

const OrderReceipt = ({
  classes,
  method,
  extra,
  cardType,
  cardAmountReceived,
  additionalPayments,
  isNewStyle,
  storeSaleType,
  isBgElevatedPackagingEligible,
  isVoidOrder,
  isSendsTakesOrder,
  isInstore,
  payments,
  gcPayments,
  vendorId,
}) => {
  const cardTypeIcon =
    vendorId === 'AFFIRM' ? getCardIconByCardType(vendorId) : cardType && getCardIconByCardType(cardType);
  const { orderTotalWithGiftCard, orderTotalAmt, giftPackaging } = method || {};

  /**
   * For BG Brand, if the sumtotal of all regular items in Checkout is more than $700, gift wrap is Free,
   * excluding sale and adjusted items from the sumtotal. (Business asked to override
   * the gift wrapping fee and total in checkout, order receipt and order confirmation page)
   * For more details see: https://neimanmarcus.atlassian.net/browse/OSP-9297
   */
  const getAmountAfterFreeGiftWrap = (amount) => {
    const amountWithFreeGiftWrap = convertStringAmountToNumber(amount) - convertStringAmountToNumber(giftPackaging);

    return (isBgElevatedPackagingEligible ? amountWithFreeGiftWrap : convertStringAmountToNumber(amount)).toFixed(2);
  };

  const renderAdditionalPayments = additionalPayments?.map(({ type, maskedCardNumber, amountReceived }) => {
    const cardTypeIcon = type && getCardIconByCardType(type);
    const extra = maskedCardNumber?.slice(-4);

    return (
      <Grid
        container
        className={classes.paymentItem}
        justifyContent="space-between"
        data-testid="order-additional-payments"
        key={`${type}${extra}`}
      >
        <Box className={classes.payments}>
          {cardTypeIcon && <Icon className={classes.cardIcon} component={cardTypeIcon} />}
          <Typography variant="h3">{extra || type}</Typography>
        </Box>
        <Typography variant="body1" color="textSecondary">
          {isVoidOrder && '- '}${getAmountAfterFreeGiftWrap(amountReceived)}
        </Typography>
      </Grid>
    );
  });

  const showExtra = !!(extra || additionalPayments?.length || cardType);

  return method ? (
    <>
      <ReceiptFields
        method={method}
        isNewStyle={isNewStyle}
        storeSaleType={storeSaleType}
        isBgElevatedPackagingEligible={isBgElevatedPackagingEligible}
        isRefundOrder={isVoidOrder}
        isSendsTakesOrder={isSendsTakesOrder}
        isInstore={isInstore}
        payments={payments}
        gcPayments={gcPayments}
      />

      {showExtra && (
        <>
          <Divider className={classes.divider} />
          <Grid container justifyContent="space-between" alignItems="center">
            <Box className={classes.payments} data-testid="order-receipt-extra">
              {cardTypeIcon && <Icon className={classes.cardIcon} component={cardTypeIcon} />}
              {vendorId !== 'AFFIRM' && <Typography variant="h3">{extra || cardType}</Typography>}
            </Box>
            <Typography
              variant="h3"
              color={cardAmountReceived.includes('-') ? 'error' : 'textPrimary'}
              data-testid="order-payments-details"
            >
              {isVoidOrder && '- '}
              {formatNumberToUSD(Number(getAmountAfterFreeGiftWrap(cardAmountReceived)))}
            </Typography>
          </Grid>
          {additionalPayments ? renderAdditionalPayments : null}
        </>
      )}
      {(renderAdditionalPayments.length > 0 || storeSaleType) && (
        <>
          <Divider className={classes.divider} />
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography className={classes.title} variant="h4" data-testid="order-receipt-total">
              Total
            </Typography>
            <Typography
              variant="h2"
              data-testid="order-receipt-total"
              color={orderTotalAmt && orderTotalAmt.includes('-') ? 'error' : 'textPrimary'}
            >
              {isVoidOrder && '- '}
              {formatNumberToUSD(Number(getAmountAfterFreeGiftWrap(orderTotalWithGiftCard || orderTotalAmt)))}
            </Typography>
          </Grid>
        </>
      )}
    </>
  ) : null;
};

OrderReceipt.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  method: ShipmentMethodPropTypes,
  extra: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  cardType: PropTypes.string,
  additionalPayments: additionalPaymentsPropType,
  cardAmountReceived: PropTypes.string,
  isNewStyle: PropTypes.bool,
  storeSaleType: PropTypes.string,
  isBgElevatedPackagingEligible: PropTypes.bool,
  isVoidOrder: PropTypes.bool,
  isSendsTakesOrder: PropTypes.bool,
  vendorId: PropTypes.string,
};

OrderReceipt.defaultProps = {
  classes: {},
  method: {},
  additionalPayments: [],
  cardAmountReceived: '0.00',
  isNewStyle: false,
  storeSaleType: '',
  isBgElevatedPackagingEligible: false,
  isVoidOrder: false,
  isSendsTakesOrder: false,
  vendorId: '',
};

export default OrderReceipt;
